/**
 * Vanilla TS versie van de React <CookieBar /> component.
 * Geen dependencies nodig (geen js-cookie, geen i18next).
 *
 * Gebruik in main.tsx, net als heroSwiper:
 *
 *   import cookieBar from "./cookieBar";
 *   cookieBar();
 *
 * Of geef een specifiek element mee om de bar in te renderen:
 *   cookieBar('#cookie-bar-container');
 */

declare global {
    interface Window {
        gtag?: (...args: unknown[]) => void;
    }
}

interface CookieBarState {
    visible: boolean;
    cookiePreferences: boolean;
    advertisingCookieValue: boolean;
}

// --- Cookie helpers (vervangt js-cookie) ---
function getCookie(name: string): string | undefined {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1] ?? '') : undefined;
}

function setCookie(name: string, value: boolean | string, days: number): void {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(String(value))}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
}

// --- GA4 consent update (ongewijzigd) ---
function setAdStorageInGa4(cookieValue: boolean): void {
    if (typeof window.gtag !== 'function') return;
    window.gtag('consent', 'update', {
        ad_storage: cookieValue ? 'granted' : 'denied'
    });
    window.gtag('event', 'page_view', {
        page_title: 'CookieSettings',
        page_location: '/cookiesettings/' + (cookieValue ? 'granted' : 'denied')
    });
}

// --- State ---
const state: CookieBarState = {
    visible: typeof getCookie('CookieConsentAdvertising') === 'undefined',
    cookiePreferences: false,
    advertisingCookieValue: getCookie('CookieConsentAdvertising') === 'true'
};

let container: HTMLElement | null = null;

function render(): void {
    if (!container) return;

    if (!state.visible) {
        container.innerHTML = '';
        return;
    }

    container.innerHTML = state.cookiePreferences ? preferencesHTML() : introHTML();
    attachEvents();
}

function introHTML(): string {
    return `
        <div class="cookie-bar">
            <div class="cookie-bar__content-wrapper">
                <div class="cookie-bar__preferences">
                    <div class="cookie-bar__content">
                        Deze website maakt gebruik van cookies en daarmee vergelijkbare technieken om een optimale gebruikerservaring te bieden. Je kunt je
                        <span class="cookie-bar__edit-preferences" data-action="edit-preferences">voorkeuren aanpassen</span>.
                    </div>
                    <div class="cookie-bar__button-container">
                        <button class="cookie-bar__button" data-action="reject">Weigeren</button>
                        <button class="cookie-bar__button cookie-bar__button--primary" data-action="accept">Accepteren</button>
                    </div>
                </div>
            </div>
        </div>
    `;
}

function preferencesHTML(): string {
    return `
        <div class="cookie-bar">
            <div class="cookie-bar__content-wrapper">
                <div class="cookie-bar__preferences">
                    <div class="cookie-bar__content">
                        <div class="cookie-bar__checkbox">
                            <input class="checkbox checkbox--disabled" type="checkbox" checked disabled />
                            <label><span><strong>Functionele cookies: </strong>Deze cookies zorgen ervoor dat de website naar behoren werkt. Deze cookies kunnen niet uitgezet worden.</span></label>
                        </div>
                        <div class="cookie-bar__checkbox">
                            <input class="checkbox checkbox--disabled" type="checkbox" checked disabled />
                            <label><span><strong>Analytische cookies: </strong>Deze cookies zorgen ervoor dat we het gebruik van de website kunnen meten en verbeteringen door kunnen voeren. Deze cookies kunnen niet uitgezet worden.</span></label>
                        </div>
                        <div class="cookie-bar__checkbox">
                            <input class="checkbox" type="checkbox" id="advertising-checkbox" ${state.advertisingCookieValue ? 'checked' : ''} />
                            <label for="advertising-checkbox"><span><strong>Advertising cookies: </strong>Deze cookie stellen onze advertentiepartners in staat om doelgerichter informatie te kunnen aanbieden.</span></label>
                        </div>
                        <div>
                            Door categorieën uit te zetten, kan het voorkomen dat gerelateerde functionaliteiten binnen de website niet langer correct werken. Het is altijd mogelijk om op een later moment de voorkeuren aan te passen.
                        </div>
                    </div>
                    <div class="cookie-bar__button-container">
                        <button class="cookie-bar__button cookie-bar__button--primary" data-action="accept-all">Alles toestaan</button>
                        <button class="cookie-bar__button" data-action="accept-selection">Selectie toestaan</button>
                    </div>
                </div>
            </div>
        </div>
    `;
}

function attachEvents(): void {
    if (!container) return;

    const editPrefs = container.querySelector<HTMLElement>('[data-action="edit-preferences"]');
    editPrefs?.addEventListener('click', () => {
        state.cookiePreferences = true;
        render();
    });

    const rejectBtn = container.querySelector<HTMLElement>('[data-action="reject"]');
    rejectBtn?.addEventListener('click', () => {
        setCookie('CookieConsentAdvertising', false, 365);
        setAdStorageInGa4(false);
        state.visible = false;
        render();
    });

    const acceptBtn = container.querySelector<HTMLElement>('[data-action="accept"]');
    acceptBtn?.addEventListener('click', () => {
        setCookie('CookieConsentAdvertising', true, 365);
        setAdStorageInGa4(true);
        state.visible = false;
        render();
    });

    const advertisingCheckbox = container.querySelector<HTMLInputElement>('#advertising-checkbox');
    if (advertisingCheckbox) {
        advertisingCheckbox.addEventListener('change', () => {
            state.advertisingCookieValue = advertisingCheckbox.checked;
        });
        const label = advertisingCheckbox.nextElementSibling as HTMLElement | null;
        label?.addEventListener('click', () => {
            advertisingCheckbox.checked = !advertisingCheckbox.checked;
            state.advertisingCookieValue = advertisingCheckbox.checked;
        });
    }

    const acceptAllBtn = container.querySelector<HTMLElement>('[data-action="accept-all"]');
    acceptAllBtn?.addEventListener('click', () => {
        setCookie('CookieConsentAdvertising', true, 365);
        setAdStorageInGa4(true);
        state.advertisingCookieValue = true;
        state.visible = false;
        render();
    });

    const acceptSelectionBtn = container.querySelector<HTMLElement>('[data-action="accept-selection"]');
    acceptSelectionBtn?.addEventListener('click', () => {
        setCookie('CookieConsentAdvertising', state.advertisingCookieValue, 365);
        setAdStorageInGa4(state.advertisingCookieValue);
        state.visible = false;
        render();
    });
}

function initCookieBar(target?: string | HTMLElement): void {
    container = typeof target === 'string'
        ? document.querySelector<HTMLElement>(target)
        : (target ?? null);

    if (!container) {
        container = document.createElement('div');
        document.body.appendChild(container);
    }

    render();

    // Zelfde gedrag als de useEffect in de React versie:
    // klik op ".button-cookie-settings" opent de bar opnieuw.
    // Via event delegation op document, zodat het ook werkt als de link
    // pas na initCookieBar() aan de DOM wordt toegevoegd.
    document.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.closest('.button-cookie-settings')) {
            e.preventDefault();
            state.visible = true;
            render();
        }
    });
}

export default initCookieBar;
